/** @jsx jsx */
import { jsx, useThemeUI } from "theme-ui"
import { useState, useEffect, useRef } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { useTranslation } from "react-i18next"

import GridRow from "../../atoms/GridRow"
import Grid from "../../atoms/Grid"
import PageWrap from "../../atoms/PageWrap"
import SEO from "../../molecules/SEO"
import ProjectThumbnail from "../../molecules/ProjectThumbnail"
import HashtagHeading from "../../molecules/HashtagHeading"
import AnimateInView from "../../atoms/AnimateInView"
import AnimateParallax from "../../atoms/AnimateParallax"
import Hero from "../../organisms/Home/Hero"
import SocialPosts from "../../organisms/Home/SocialPosts"

import { getDataForLocale, localizeUrl } from "../../../utils/i18n"

const views = { list: "list", globe: "globe" }

const IndexPage = ({ view: viewProps, location, pageContext }) => {
  const { theme } = useThemeUI()
  const { t } = useTranslation()
  const [view, setView] = useState(viewProps)
  const [transformOrigin, setTransformOrigin] = useState(0)
  const containerElRef = useRef()
  const containerElBbRef = useRef()
  const [containerHeight, setContainerHeight] = useState()
  const data = useStaticQuery(query)
  const {
    allSanityProject: { nodes: projects },
    allSanityProjectCategory: { nodes: projectsCategories },
    allSanityLive: { nodes: posts },
    sanityHomePage,
  } = getDataForLocale(data, pageContext.locale)
  const [categories, setCategories] = useState(
    projectsCategories.map(x => x.id)
  )

  const isMobile =
    typeof window !== "undefined" &&
    !window.matchMedia(`screen and (min-width: ${theme.breakpoints[0]})`)
      .matches // see: https://github.com/styled-system/styled-system/blob/54730697d0648707c89e719ea7edeef9e3d294f2/packages/core/src/index.js#L26

  const handleViewChange = newView => {
    setView(newView)
    // hijack Gatsby routing
    window.history.pushState(
      {},
      document.title,
      localizeUrl(t, { url: newView === views.list ? "/" : `/${newView}` })
    )
  }

  const handleCategoriesChange = categoryId => {
    categories.find(_categoryId => _categoryId === categoryId)
      ? setCategories(
          categories.filter(_categoryId => _categoryId !== categoryId)
        )
      : setCategories([...categories, categoryId])
  }

  useEffect(() => {
    containerElBbRef.current = containerElRef.current.getBoundingClientRect()
    setContainerHeight(containerElBbRef.current.height)
  }, [])

  useEffect(() => {
    const pos =
      document.documentElement.clientHeight / 2 - containerElBbRef.current.top

    setTransformOrigin(pos)
  }, [view])

  useEffect(() => {
    handleViewChange(
      location.pathname.match(/globe/) ? views.globe : views.list
    )
  }, [location])

  return (
    <PageWrap sx={{ overflow: "hidden" }}>
      <SEO
        title="Home"
        pathname={location.pathname}
        bodyAttributes={{
          className: "overflow-hidden",
        }}
      />
      <Hero
        isMobile={isMobile}
        view={view}
        parentRef={containerElRef}
        categories={categories}
        onViewChange={handleViewChange}
        onCategoriesChange={handleCategoriesChange}
        data={{ ...sanityHomePage, projects, projectsCategories, posts }}
      />
      <div ref={containerElRef}>
        <GridRow
          id="events"
          sx={{
            transitionDuration: "0.5s",
            transitionProperty: "transform, opacity",
            transformOrigin: `center ${transformOrigin}px`,
            ...(view === views.globe
              ? {
                  transitionTimingFunction: "ease-in",
                  transform: "perspective(10px) translateZ(10px)",
                  opacity: 0,
                }
              : {
                  transitionDuration: "0.4s",
                  transitionDelay: "0.5s",
                  transitionTimingFunction: "cubic-bezier(0, 0, 0.34, 1.01)",
                  transform: "perspective(10px) translateZ(0)",
                  opacity: 1,
                }),
          }}
        >
          <Grid
            sx={{
              gridTemplateColumns: ["repeat(8, 1fr)", "repeat(20, 1fr)"],
              gridColumn: ["1 / -1", "3 / -3"],
              mt: 8,
            }}
          >
            {projects.map((project, i) => (
              <GridRow.Col
                key={project.slug.current}
                sx={{
                  gridColumn: ["1 / -1", `${i % 2 ? 12 : "auto"} / span 9`],
                  position: "relative",
                  top: [0, `${i % 2 ? "140px" : 0}`],
                }}
              >
                <div sx={{ mb: 7 }}>
                  <AnimateInView y="70px" delay={i % 2 ? "0.1s" : "0"}>
                    <AnimateParallax index={isMobile ? 1 : i}>
                      <ProjectThumbnail project={project} index={i} />
                    </AnimateParallax>
                  </AnimateInView>
                </div>
              </GridRow.Col>
            ))}
          </Grid>
          <Grid
            sx={{
              position: "relative",
              gridTemplateColumns: ["repeat(8, 1fr)", "repeat(20, 1fr)"],
              gridColumn: ["1 / -1", "1 / -1", "1 / -1", "2 / -2"],
              pb: 7,
              "::after": {
                content: '""',
                position: "absolute",
                zIndex: -1,
                top: 0,
                left: "50%",
                width: "100vw",
                height: "100%",
                transform: "translateX(-50%)",
                background:
                  "linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #ffffff 40%)",
              },
            }}
          >
            <HashtagHeading
              styleSx={{
                gridColumn: "1 / -1",
                textAlign: "center",
                my: 7,
              }}
              liveSubtitle={sanityHomePage._rawLiveSubtitle}
            />
            <SocialPosts posts={posts.slice(0, 4)} isMobile={isMobile} />
          </Grid>
        </GridRow>
      </div>
    </PageWrap>
  )
}

export default IndexPage

export const query = graphql`
  query AllProjects {
    sanityHomePage {
      subtitle {
        ...StringAllLanguages
      }
      scrollHint {
        ...StringAllLanguages
      }
      tapHint {
        ...StringAllLanguages
      }
      _rawLiveSubtitle(resolveReferences: { maxDepth: 5 })
    }
    allSanityProject(sort: { fields: date, order: DESC }) {
      nodes {
        ...ProjectThumbnail
        location {
          lat
          lng
        }
        headline {
          ...StringAllLanguages
        }
        excerpt {
          ...StringAllLanguages
        }
        category {
          id
          style
        }
      }
    }
    allSanityProjectCategory {
      nodes {
        name
        id
      }
    }
    allSanityLive(sort: { order: DESC, fields: _updatedAt }) {
      nodes {
        id
        livePost {
          ...SocialPost
        }
        headline {
          ...StringAllLanguages
        }
        excerpt {
          ...StringAllLanguages
        }
        location {
          lat
          lng
        }
        category {
          id
          style
        }
      }
    }
  }
`
